import { Box, Modal, Text, Textarea } from '@tocktix/pantry';
import { Form, Formik, FormikErrors } from 'formik';
import { FunctionComponent, useState } from 'react';
import { userClient } from 'tools/bff/client';

interface ReactivateUserFormValues {
  reason: string;
}

interface ReactivateUserModalProps {
  uuid: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const validate = (values: ReactivateUserFormValues) => {
  const errors: FormikErrors<ReactivateUserFormValues> = {};
  if (!values.reason.trim()) {
    errors.reason = 'Reason is required';
  }
  return errors;
};

const ReactivateUserModal: FunctionComponent<ReactivateUserModalProps> = ({
  uuid,
  open,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmission = (values: ReactivateUserFormValues) => {
    return userClient
      .activateUsers({ uuids: [uuid], reason: values.reason.trim() })
      .then(onSubmit)
      .then(handleClose)
      .catch((reason) => setError('Error reactivating user: ' + reason));
  };

  return (
    <Formik<ReactivateUserFormValues>
      initialValues={{ reason: '' }}
      validate={validate}
      onSubmit={handleSubmission}
    >
      {({ dirty, errors, handleSubmit, isSubmitting, isValid, setFieldValue, touched, values }) => (
        <Form id="reactivate-user-form" data-testid="reactivate-user-form">
          <Modal
            id="reactivate-user-modal"
            open={open}
            onClose={handleClose}
            size="md"
            data-testid="reactivate-user-modal"
          >
            <Modal.Header title="Reactivate account" onClose={handleClose} border />
            <Modal.Body>
              <Box display="flex" flexDirection="column" gap={200}>
                <Text variant="body" color="fgSubtle">
                  This will reactivate the account and restore user access.
                </Text>
                <Textarea
                  id="reactivate-user-reason"
                  label="Reason"
                  value={values.reason}
                  onChange={(event) => setFieldValue('reason', event.target.value)}
                  required
                  fullWidth
                  data-testid="reactivate-user-reason-input"
                  validationStatus={touched.reason && errors.reason ? 'error' : undefined}
                  validationMessage={touched.reason ? errors.reason : undefined}
                />
                {error && (
                  <Text variant="body" color="fgDangerBase">
                    {error}
                  </Text>
                )}
              </Box>
            </Modal.Body>
            <Modal.Footer
              actions={{
                primary: {
                  label: isSubmitting ? 'Reactivating...' : 'Reactivate',
                  type: 'submit',
                  disabled: !dirty || !isValid || isSubmitting,
                  onClick: () => handleSubmit(),
                },
                cancel: {
                  label: 'Cancel',
                  onClick: handleClose,
                },
              }}
            />
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default ReactivateUserModal;
